import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from '@mui/icons-material/Help';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { ChartEmptyState } from '@/ui/composites';
import { StatusPieCardSkeleton } from '@/ui/composites/status/StatusPieCardSkeleton';
import {
  Box,
  BuildsIcon,
  LineChartSkeleton,
  Skeleton,
  SuitesFilledIcon,
  TestsFilledIcon,
} from '@/ui/primitives';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { MetricsScope } from '@/features/metrics/metricsHelpers';
import type { SvgIconComponent } from '@mui/icons-material';
import type { ReactElement } from 'react';

const LONG_SKELETON_HEIGHT = componentSizes.dataGrid.skeletonHeight;
const LONG_SKELETON_RADIUS = '999px';

export const renderMiniMetricEmptyState = (title: string, height: number): ReactElement => (
  <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <ChartEmptyState
      title={title}
      contentSx={{ transform: 'translateY(-6px)' }}
      titleSx={{ fontWeight: 500 }}
    />
  </Box>
);

export const renderMiniMetricLoadingState = (height: number): ReactElement => (
  <LineChartSkeleton
    height={height}
    leftInset={`calc(${s('md')} + ${s('none')})`}
    bottomInset={s('md')}
  />
);

export const renderStatusPieLoadingState = (height: number): ReactElement => {
  const legendWidths = ['94%', '94%'] as const;
  return (
    <Box
      sx={{
        width: '100%',
        height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: s('2xl'),
        pt: s('2xl'),
      }}
    >
      <StatusPieCardSkeleton />
      <Box
        sx={{
          width: '100%',
          maxWidth: '72%',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          columnGap: s('sm'),
          px: s('lg'),
          pt: s('md'),
          justifyContent: 'center',
          justifyItems: 'center',
          mx: 'auto',
        }}
      >
        {legendWidths.map((width, index) => (
          <Skeleton
            key={`pie-legend-skeleton-${index}`}
            variant="rounded"
            width={width}
            height={LONG_SKELETON_HEIGHT}
            sx={{
              display: 'block',
              flexShrink: 0,
              minHeight: LONG_SKELETON_HEIGHT,
              maxHeight: LONG_SKELETON_HEIGHT,
              borderRadius: LONG_SKELETON_RADIUS,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export const renderStatusChartLegendLoadingState = (): ReactElement => {
  const itemWidths = [78, 78, 78, 78] as const;
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        alignItems: 'center',
        columnGap: s('md'),
        rowGap: s('xs'),
        minWidth: 0,
        pr: s('xl'),
        boxSizing: 'border-box',
      }}
    >
      {itemWidths.map((width, index) => (
        <Skeleton
          key={`status-legend-skeleton-${index}`}
          variant="rounded"
          width={width}
          height={LONG_SKELETON_HEIGHT}
          sx={{
            display: 'block',
            flexShrink: 0,
            minHeight: LONG_SKELETON_HEIGHT,
            maxHeight: LONG_SKELETON_HEIGHT,
            borderRadius: LONG_SKELETON_RADIUS,
          }}
        />
      ))}
    </Box>
  );
};

export const renderMiniMetricHeaderLoadingState = (): ReactElement => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: s('md'),
    }}
  >
    <Skeleton
      variant="rounded"
      width={92}
      height={LONG_SKELETON_HEIGHT}
      sx={{ borderRadius: LONG_SKELETON_RADIUS }}
    />
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <Skeleton
        variant="rounded"
        width={78}
        height={LONG_SKELETON_HEIGHT}
        sx={{ borderRadius: LONG_SKELETON_RADIUS }}
      />
    </Box>
  </Box>
);

export const formatStackedStatusChartLabel = (label: string): string => {
  const trimmed = label.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}`;
  }
  const slashMatch = /^(\d{1,2})\/(\d{1,2})(?:\/\d{4})?$/.exec(trimmed);
  if (slashMatch) {
    const day = slashMatch[1].padStart(2, '0');
    const month = slashMatch[2].padStart(2, '0');
    return `${day}/${month}`;
  }
  return trimmed;
};

export const resolveStackedStatusCardIcon = (
  statusId: string,
  scope: MetricsScope,
): SvgIconComponent => {
  if (statusId === 'passed' || statusId === 'completed') return CheckCircleIcon;
  if (statusId === 'failed') return CancelIcon;
  if (statusId === 'errored') return ErrorIcon;
  if (statusId === 'running') return PlayCircleFilledWhiteIcon;
  if (statusId === 'queued') return ScheduleIcon;
  if (statusId === 'none') return DonutLargeIcon;
  if (statusId === 'skipped') return BlockOutlinedIcon;
  if (statusId === 'untested') return HelpIcon;
  if (statusId === 'total' && scope === 'executionRun') return PlayCircleFilledWhiteIcon;
  if (statusId === 'total' && scope === 'pipeline') return BuildsIcon;
  if (statusId === 'total' && scope === 'suite') return SuitesFilledIcon;
  if (statusId === 'total' && scope === 'test') return TestsFilledIcon;
  return DonutLargeIcon;
};

export const resolveStackedStatusCardIconColor = (
  statusTone: MetricCardItem['statusTone'],
): string => {
  if (statusTone === 'success') return uiTokens.statusColors.green;
  if (statusTone === 'error') return uiTokens.statusColors.red;
  if (statusTone === 'warning') return uiTokens.statusColors.orange;
  if (statusTone === 'brown') return uiTokens.statusColors.brown;
  if (statusTone === 'primary') return uiTokens.statusColors.teal;
  if (statusTone === 'info') return uiTokens.statusColors.blue;
  return uiTokens.statusColors.neutral;
};
